import React from 'react';
import { Typography, Grid, Chip, Box } from '@mui/material';
import SemiPieChart from 'lib/designSystem/SemiPieChart';
import { formatDate, formatMoney, showAlert } from 'lib/functions/utils';
import Card from 'lib/designSystem/Card';
import { useDispatch, useSelector } from 'react-redux';
import { getCreditLineByProduct } from 'lib/api/creditLine';
import { RootState } from 'services/redux/store';
import { getUser } from 'lib/api';
import { useEffect, useState, useRef } from 'react';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { postCreditLineExpress } from 'lib/api/creditLine';
import CustomButton from 'lib/designSystem/Button';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

export const STATUS_COLORS = {
  VALIDATING: 'warning',
  IN_REVIEW: 'info',
  ACTIVE: 'success',
  REJECTED: 'error',
} as const;

export const STATUS_LABELS = {
  VALIDATING: 'En validación',
  IN_REVIEW: 'En revisión',
  ACTIVE: 'Activa',
  REJECTED: 'Rechazada',
} as const;

export const STATUS_ICONS = {
  VALIDATING: AccessTimeIcon,
  IN_REVIEW: AssignmentIcon,
  ACTIVE: CheckCircleIcon,
  REJECTED: CancelIcon,
} as const;

export type SubProductId =
  | 'buy-order'
  | 'check-invoice'
  | 'contract'
  | 'electronic-invoice'
  | 'pending-account'
  | 'promissory-note'
  | 'check'
  | 'credit-request-cuota'
  | 'offer-seriousness'
  | 'faithful-compliance'
  | 'advance'
  | 'warranty-premium'
  | 'proper-performance'
  | 'faithful-compliance-performance';

export const SUBPRODUCT_LABELS: Record<SubProductId, string> = {
  'buy-order': 'Orden de compra',
  'check-invoice': 'Factura con flujo de cheques',
  contract: 'Contrato',
  'electronic-invoice': 'Factura Electrónica',
  'pending-account': 'Cuenta pendiente',
  'promissory-note': 'Pagaré',
  check: 'Cheque',
  'credit-request-cuota': 'Cuota de crédito',
  'offer-seriousness': 'Seriedad de oferta',
  'faithful-compliance': 'Fiel cumplimiento',
  advance: 'Anticipo',
  'warranty-premium': 'Costo emisión certificado',
  'proper-performance': 'Correcta ejecución',
  'faithful-compliance-performance': 'Fiel cumplimiento y correcta ejecución',
};

// Types
interface SubProduct {
  subProductId: SubProductId;
  amount: number;
  used: number;
}

interface CreditLineData {
  status: 'VALIDATING' | 'IN_REVIEW' | 'ACTIVE' | 'REJECTED';
  amount: number;
  used: number;
  validUntil: string;
  subProducts: SubProduct[];
}

// Custom Hook
const useCreditLine = ({ productId }: { productId: string }) => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const [creditLineData, setCreditLineData] = useState<CreditLineData | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);

  const fetchCreditLine = async () => {
    try {
      setIsLoading(true);
      const profile = await getUser(dispatch, { id: auth.user?.id });
      if (!profile) throw new Error('Profile not found');
      const businessIdentifier = profile.company.taxIdentifier;
      const response = await getCreditLineByProduct(
        dispatch,
        businessIdentifier,
        productId,
      );
      if (response) setCreditLineData(response);
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al obtener la línea de crédito');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const createCreditLine = async () => {
    try {
      const profile = await getUser(dispatch, { id: auth.user?.id });
      if (!profile) throw new Error('Profile not found');
      const businessIdentifier = profile.company.taxIdentifier;
      if (!businessIdentifier) throw new Error('Business identifier not found');
      await postCreditLineExpress(dispatch, {
        businessIdentifier,
        productType: productId,
      });
      await fetchCreditLine();
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCreditLine();
  }, []);

  return { creditLineData, isLoading, createCreditLine };
};

// Utility Functions
const formatPercentage = (used: number, total: number): string => {
  return `${Math.round((used / total) * 100)}%`;
};

// Components
const ProductMainInfo: React.FC<{ amount: number; used: number }> = ({
  amount,
  used,
}) => {
  const hasValidAmount = amount && amount !== 0;
  const hasValidUsed = used && used !== 0;
  const displayAmount = hasValidAmount ? formatMoney(amount) : 'Por definir';
  const displayPercentage =
    hasValidAmount && hasValidUsed
      ? formatPercentage(Number(used), Number(amount))
      : '-';

  return (
    <Grid
      container
      style={{
        justifyContent: 'space-between',
        padding: '0 4px',
        marginTop: 10,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        <span style={{ fontWeight: 600 }}>Monto Total:</span> {displayAmount}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        <span style={{ fontWeight: 600 }}>Porcentaje Uso:</span>{' '}
        {displayPercentage}
      </Typography>
    </Grid>
  );
};

const NoCreditLine: React.FC<{ createCreditLine: () => Promise<void> }> = ({
  createCreditLine,
}) => {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="60vh"
      flexDirection="column"
      gap={2}
    >
      <Typography variant="h4" gutterBottom color="text.primary">
        No tienes una línea de crédito activa
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Solicita tu línea de crédito para acceder a nuestros productos
      </Typography>
      <CustomButton color="primary" onClick={createCreditLine} size="large">
        Solicitar Línea de Crédito
      </CustomButton>
    </Box>
  );
};

const CreditLineChart: React.FC<{ data: CreditLineData; status: string }> = ({
  data,
  status,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(280);
  const statusLabel = STATUS_LABELS[status as keyof typeof STATUS_LABELS];
  const StatusIcon = STATUS_ICONS[status as keyof typeof STATUS_ICONS];

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const totalData = [
    { name: 'Utilizado', value: data.used },
    { name: 'Disponible', value: data.amount - data.used },
  ];

  return (
    <div style={{ paddingTop: '18px' }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <Grid container justifyContent="space-between" alignItems="center">
              <Typography
                variant="h4"
                style={{ textAlign: 'center', marginBottom: 2 }}
              >
                Monto Total
              </Typography>
              <Chip
                icon={
                  <StatusIcon
                    style={{
                      color: 'white',
                      fontSize: '16px',
                      marginLeft: '8px',
                    }}
                  />
                }
                label={statusLabel}
                color={STATUS_COLORS[status as keyof typeof STATUS_COLORS]}
              />
            </Grid>

            <ProductMainInfo amount={data.amount} used={data.used} />
            <div ref={containerRef}>
              <SemiPieChart
                data={totalData}
                money={true}
                width={containerWidth}
                height={200}
                cx={containerWidth / 2}
                cy={100}
              />
            </div>
          </Card>
          {data.validUntil && (
            <Typography
              variant="body1"
              color="text.secondary"
              gutterBottom
              style={{ marginTop: '10px' }}
            >
              Valida hasta:{' '}
              {formatDate(new Date(data.validUntil))}
            </Typography>
          )}
        </Grid>

        {data.subProducts.map((product) => {
          const subProductData = [
            { name: 'Utilizado', value: product.used },
            { name: 'Disponible', value: product.amount - product.used },
          ];

          return (
            <Grid item xs={12} md={4} key={product.subProductId}>
              <Card>
                <Grid container justifyContent="space-between">
                  <Typography
                    variant="h4"
                    style={{ textAlign: 'center', marginBottom: 2 }}
                  >
                    {SUBPRODUCT_LABELS[product.subProductId]}
                  </Typography>
                </Grid>
                <ProductMainInfo amount={product.amount} used={product.used} />
                <div ref={containerRef}>
                  <SemiPieChart
                    data={subProductData}
                    money={true}
                    width={containerWidth}
                    height={200}
                    cx={containerWidth / 2}
                    cy={100}
                  />
                </div>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
};

// Main Component
const CreditLineStatus: React.FC<{ productId: string }> = ({ productId }) => {
  const { creditLineData, isLoading, createCreditLine } = useCreditLine({
    productId,
  });

  if (isLoading) return <CenteredCircularProgress />;
  if (!creditLineData?.status)
    return <NoCreditLine createCreditLine={createCreditLine} />;

  const status = creditLineData.status;

  return <CreditLineChart data={creditLineData} status={status} />;
};

export default CreditLineStatus;
